import { createReducer, on } from '@ngrx/store';
import { ComponentState } from '../../shared/types/ComponentState';
import { Constants, StwProcessCode } from '../../shared/utilities/Constants';
import {
  StandingAuthConfirmationPageActions,
  StandingAuthFormActions,
  StandingAuthInternalTransferActions,
  StandingAuthReviewPageActions,
} from '../actions';
import { InternalTransferInfoSA } from '../models/InternalTransferInfoSA';
import { AuthResponse } from '../../create-request/models/shared/AuthResponse';
import {InitReleaseActions} from "../../initiate-and-release/actions";

export const standingAuthitInfoKey = 'internalTransferInfo';

export type State = InternalTransferInfoSA;

export const intialComponentState: ComponentState = {
  loading: false,
  error: '',
  hasError: false,
};

export const initialStandingAuthInternalTransfersInfoState: State = {
  inputCallFinished: false,
  toAccountInfo: {
    accountNumber: '',
    registrationType: '',
    regTypeShortName: '',
    productCode: '',
    isIdentical: false,
    isRelated: false,
    isIraAccount: false,
    isTrust: false,
    toIncome: '100%',
    toPrincipal: '0%',
    accountRegistration: [],
    availableCashMmf: '',
  },
  loadToAccountErrorText: '',
  toAccountState: intialComponentState,
  verifyCallHasError: false,
  verifyCallErrorText: '',
  verifyCallFinished: false,
  clientAuthorizationNeeded: false,
  eauthAccess: false,
  hasWpfaRole: false,
  eAuthModel: {
    isTransactionFromSLOA: false,
    eauthSelected: false,
    isTransactionFromSLOAOrSLOA: false,
    numberOfAuthRequired: 0,
    eauthEligibilityServiceSLOAError: false,
    eauthEligibilityServiceDown: false,
    eauthEligibilityServiceSLOAErrorMsg: '',
    isOrgAccount: false,
    accountRestricted: false,
    orgOrEduSavingsAccount: false,
    custAcctAuthorizationsListEmpty: false,
    eauthInd: false,
    eauthSLOAInd: false,
    allAccountHolderIneligible: false,
    numOfIneligibleCust: 0,
    eauthEligibilityServiceDownErrorMsg: '',
    custAcctAuthorizationsList: {
      allAccountHoldersIneligible: false,
      numOfIneligibleCust: 0,
      numOfNotWebEnabledCust: 0,
      totalNumOfAccountHolders: 0,
      eauthInd: false,
      eAuthCustomerList: [],
    },
    custAcctSLOAAuthorizationsList: {
      allAccountHoldersIneligible: false,
      numOfIneligibleCust: 0,
      numOfNotWebEnabledCust: 0,
      totalNumOfAccountHolders: 0,
      eauthInd: false,
      eAuthCustomerList: [],
    },
    transactionType: '',
    submitButtonsDisable: '',
    txnType: '',
  },
  iraFields: {
    isIraTransaction: false,
    isAccountTaxable: false,
    hasActiveTaxElections: false,
    fedTaxPercent: '',
    fedTaxAmount: '',
    isWithholdFedTax: false,
    stateTaxPercent: '',
    stateTaxAmount: '',
    stateTaxAttentionRequired: false,
    isWithholdStateTax: false,
    stateTypeIndicator: '',
    stwProcessCode: '',
    stateTaxAttentionMessage: '',
    isCashieringIRAAgreementOpened: false,
    rmdAnnualAmount: '',
    rmdAnnualPaidYearToDateAmount: '',
    isRmdToDisplay: false,
    taxValueOriginMessage: '',
    distributionReason: {
      code: '',
      subCode: '',
      description: '',
      isTaxExempt: false,
    },
    distributionReasonMap: [],
    nraAccountIndicator: false,
  },
  messageInfo: {
    errorList: [],
    warningList: [],
    atsSystemErrors: [],
  },
  pendReasons: [],
  shouldShowStateTax: false,
  stateCd: '',
  isThirdParty: false,
  requestedDate: '',
  isPendingOnlyForCTWE: false,
  journalAuthResponse: {
    draftId: '',
    pendForRelease: false,
    pendForReleaseReason: '',
    canRelease: false
  },
  revalidation: {
    status: '',
    errors: [],
    fieldsUpdated: []
 }
};

export const standingAuthInternalTransferInfoReducer = createReducer(
  initialStandingAuthInternalTransfersInfoState,
  on(
    StandingAuthInternalTransferActions.loadToAccountSuccess,
    (state, response) => {
      const { internalTransferInputResponse } = response;
      const toAccountHasPtrsOffer = internalTransferInputResponse?.toAccountInfo?.isTrust && !internalTransferInputResponse?.isTrustNonPTRSAccount;
      const defaultToIncome = toAccountHasPtrsOffer ? '0%' : '100%';
      const defaultToPrincipal = toAccountHasPtrsOffer ? '100%' : '0%';
      return {
        ...state,
        toAccountInfo: {
          ...internalTransferInputResponse.toAccountInfo,
          toIncome:
            internalTransferInputResponse?.toAccountInfo?.toIncome.length !== 0
              ? internalTransferInputResponse?.toAccountInfo?.toIncome
              : defaultToIncome,
          toPrincipal:
            internalTransferInputResponse?.toAccountInfo?.toPrincipal.length !==
              0
              ? internalTransferInputResponse?.toAccountInfo?.toPrincipal
              : defaultToPrincipal,
        },
        inputCallFinished: true,
        toAccountState: intialComponentState,
        loadToAccountErrorText: '',
        hasWpfaRole: internalTransferInputResponse?.hasWpfaRole,
        iraFields: {
          ...internalTransferInputResponse.iraFields,
          fedTaxPercent: internalTransferInputResponse.iraFields.fedTaxAmount,
          fedTaxAmount: '',
          isWithholdFedTax: false,
          stateTaxPercent:
            internalTransferInputResponse.iraFields.stateTaxAmount,
          stateTaxAmount: '',
          isWithholdStateTax: false
        },
        shouldShowStateTax: hasStateTax(
          internalTransferInputResponse.iraFields.nraAccountIndicator,
          internalTransferInputResponse.iraFields
            .stwProcessCode as StwProcessCode
        ),
        stateCd: internalTransferInputResponse.stateCd,
      };
    }
  ),
  on(
    StandingAuthInternalTransferActions.loadToAccountFinishedWithError,
    (state, response) => {
      const { internalTransferInputResponse } = response;
      const toAccountHasPtrsOffer = internalTransferInputResponse?.toAccountInfo?.isTrust && !internalTransferInputResponse?.isTrustNonPTRSAccount;
      const defaultToIncome = toAccountHasPtrsOffer ? '0%' : '100%';
      const defaultToPrincipal = toAccountHasPtrsOffer ? '100%' : '0%';
      return {
        ...state,
        loadToAccountErrorText: internalTransferInputResponse.errorText,
        toAccountInfo: {
          ...internalTransferInputResponse.toAccountInfo,
          toIncome:
            internalTransferInputResponse?.toAccountInfo?.toIncome.length !== 0
              ? internalTransferInputResponse?.toAccountInfo?.toIncome
              : defaultToIncome,
          toPrincipal:
            internalTransferInputResponse?.toAccountInfo?.toPrincipal.length !==
              0
              ? internalTransferInputResponse?.toAccountInfo?.toPrincipal
              : defaultToPrincipal,
        },
        toAccountState: intialComponentState,
      };
    }
  ),
  on(StandingAuthInternalTransferActions.toAccountEntered, (state) => {
    return {
      ...state,
      inputCallFinished: false,
      loadToAccountErrorText: '',
      toAccountInfo:
        initialStandingAuthInternalTransfersInfoState.toAccountInfo,
      toAccountState: {
        loading: true,
        error: '',
        hasError: false,
      },
    };
  }),
  on(StandingAuthInternalTransferActions.loadToAccountFail, (state, err) => {
    return {
      ...state,
      toAccountState: {
        loading: false,
        error: err.message,
        hasError: true,
      },
    };
  }),
  on(
    StandingAuthInternalTransferActions.clearToAccount,
    StandingAuthReviewPageActions.cancelClicked,
    StandingAuthConfirmationPageActions.createNewStandingAuthClicked,
    () => {
      return initialStandingAuthInternalTransfersInfoState;
    }
  ),
  on(
    StandingAuthInternalTransferActions.callInternalTransferVerify,
    (state) => {
      return {
        ...state,
        verifyCallHasError: false,
        verifyCallErrorText: '',
        verifyCallFinished: false,
      };
    }
  ),
  on(
    StandingAuthInternalTransferActions.clearStandingAuthInternalTransferResponse,
    (state) => {
      return {
        ...state,
        verifyCallHasError: false,
        verifyCallErrorText: '',
        verifyCallFinished: false,
        journalAuthResponse: initialStandingAuthInternalTransfersInfoState.journalAuthResponse
      };
    }
  ),
  on(
    StandingAuthInternalTransferActions.verifyCallFinishedWithError,
    (state, { response }) => {
      return {
        ...state,
        verifyCallHasError: response.hasError,
        verifyCallErrorText:
          response.messageInfo.atsSystemErrors.length > 0
            ? Constants.createRequestServiceError
            : response.messageInfo.errorList[0]?.messageText,
        verifyCallFinished: false,
        revalidation: response.revalidation
      };
    }
  ),
  on(StandingAuthInternalTransferActions.loadInternalTransferReviewPageWithoutVerifyCall, (state, _form) => {
    const today = new Date();
    return {
      ...state,
      verifyCallHasError: false,
      verifyCallErrorText: '',
      verifyCallFinished: true,
      requestedDate: today.toLocaleDateString()
    }
  }),
  on(
    StandingAuthInternalTransferActions.verifyCallSuccessful,
    (state, { response }) => {
      return {
        ...state,
        verifyCallHasError: response.hasError,
        verifyCallErrorText: '',
        verifyCallFinished: true,
        iraFields: {
          ...state.iraFields,
          ...response.iraFields,
          fedTaxPercent: response.iraFields.fedTaxAmount,
          isWithholdFedTax: response.iraFields.isWithholdFedTax,
          stateTaxPercent: response.iraFields.stateTaxAmount,
          isWithholdStateTax: response.iraFields.isWithholdStateTax,
        },
        shouldShowStateTax: hasStateTax(
          response.iraFields.nraAccountIndicator,
          response.iraFields
            .stwProcessCode as StwProcessCode
        ),
        toAccountInfo: response.toAccountInfo,
        clientAuthorizationNeeded: response.clientAuthorizationNeeded,
        eauthAccess: response.eauthAccess,
        eAuthModel: response.eAuthModel,
        messageInfo: response.messageInfo,
        pendReasons: response.pendReasons,
        isThirdParty: response.isThirdParty,

        isPendingOnlyForCTWE: response.isPendingOnlyForCTWE,
        journalAuthResponse: response.journalAuthResponse,
        revalidation: response.revalidation
      };
    }
  ),
  on(
    StandingAuthInternalTransferActions.acknowledgeCallSuccessful,
    (state, { response }) => {
      return {
        ...state,
        acknowledgeCallFinished: true,
        requestedDate: response.requestedDate,
        pendReasons: response.pendReasons
      };
    }
  ),
  on(StandingAuthReviewPageActions.editClicked, (state) => {
    return {
      ...state,
      verifyCallFinished: false,
    };
  }),
  on(
    StandingAuthFormActions.transactionTypeChanged,
    (_) => initialStandingAuthInternalTransfersInfoState
  ),
  on(InitReleaseActions.initReleaseByDraftIdSuccessful, (state, { response, isSloa }) => {
    if (!isSloa || response.transactionType !== 'journal') return state;
    let draftObject = response.draftObject;
    return {
      ...state,
      iraFields: {
        ...state.iraFields,
        isWithholdFedTax: !!draftObject.isWithholdFedTax,
        isWithholdStateTax: !!draftObject.isWithholdStateTax,
        isIraTransaction: !!draftObject.isIraTransaction,
        fedtaxpercent: response.draftObject.fedTaxAmount ? `${response.draftObject.fedTaxAmount.toString()}%` : '',
        stateTaxPercent: response.draftObject.stateTaxAmount ? `${response.draftObject.stateTaxAmount.toString()}%` : ''
      },
      toAccountInfo: {
        ...state.toAccountInfo,
        accountNumber: draftObject.toAccountId?.toString() ?? ''
      }
    }
  })
);

function hasStateTax(
  nraIndicator: boolean,
  stwProcessCode: StwProcessCode
): boolean {
  if (nraIndicator) {
    return true;
  } else if (stwProcessCode) {
    return stwProcessCode !== 'N';
  } else {
    return false;
  }
}
